import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import PlatformHubTabs from "../components/PlatformHubTabs";
import { api } from "../lib/api";
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Banknote, CalendarDays, MousePointerClick, RefreshCw, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatDate = (value) => value ? new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString() : "—";
const formatMoney = (amount, currency) => `${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();
const monthFromLabel = (label) => {
  const parsed = new Date(`${label} 1`);
  return Number.isNaN(parsed.getTime()) ? "" : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
};

function Kpi({ label, value, icon: Icon, detail }) {
  return (
    <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-zinc-400"><Icon size={14} /> {label}</div>
      <div className="mt-2 text-2xl font-bold text-white">{value}</div>
      {detail && <div className="mt-1 text-xs text-zinc-500">{detail}</div>}
    </section>
  );
}

export default function TheBlowersHub() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currency, setCurrency] = useState("EUR");

  const loadData = async () => {
    try {
      const response = await api.get("/api/theblowers/stats");
      setData(response.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load TheBlowers extension data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const refreshFromExtension = async () => {
    setRefreshing(true);
    try {
      const response = await api.post("/api/theblowers/refresh-from-extension", {});
      setData(response.data || null);
      toast.success("TheBlowers extension data refreshed");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "No extension payload is available yet");
    } finally {
      setRefreshing(false);
    }
  };

  const stats = data?.stats || {};
  const paymentsData = data?.payments || {};
  const payments = Array.isArray(paymentsData.payments) ? paymentsData.payments : [];
  const monthlyRevenue = Array.isArray(data?.paymentsByMonth) ? data.paymentsByMonth : [];
  const dailyRegistrations = Array.isArray(stats.dailyRegistrations) ? stats.dailyRegistrations : [];
  const monthlyRegistrations = Array.isArray(stats.monthlyRegistrations) ? stats.monthlyRegistrations : [];
  const currencies = [...new Set([
    ...Object.keys(data?.totalsByCurrency || {}),
    ...monthlyRevenue.map((item) => item.currency).filter(Boolean),
  ])];

  useEffect(() => {
    if (currencies.length && !currencies.includes(currency)) setCurrency(currencies[0]);
  }, [currencies.join(","), currency]);

  const years = [...new Set([
    ...dailyRegistrations.map((item) => item.date?.slice(0, 4)),
    ...monthlyRegistrations.map((item) => monthFromLabel(item.month).slice(0, 4)),
    ...monthlyRevenue.map((item) => item.month?.slice(0, 4)),
  ].filter(Boolean))].sort().reverse();
  const monthInRange = (key) => key
    && (!year || year === "all" || key.startsWith(year))
    && (!month || month === "all" || key.slice(5, 7) === month)
    && (!dateFrom || `${key}-31` >= dateFrom)
    && (!dateTo || `${key}-01` <= dateTo);

  const filteredDaily = useMemo(() => dailyRegistrations.filter((item) => {
    const date = item.date || "";
    return (!year || year === "all" || date.startsWith(year))
      && (!month || month === "all" || date.slice(5, 7) === month)
      && (!dateFrom || date >= dateFrom)
      && (!dateTo || date <= dateTo);
  }), [dailyRegistrations, year, month, dateFrom, dateTo]);

  const filteredMonthlyRegistrations = useMemo(() => monthlyRegistrations.filter((item) =>
    monthInRange(monthFromLabel(item.month || ""))
  ), [monthlyRegistrations, year, month, dateFrom, dateTo]);

  const filteredMonthlyRevenue = useMemo(() => monthlyRevenue.filter((item) =>
    item.currency === currency && monthInRange(item.month)
  ), [monthlyRevenue, currency, year, month, dateFrom, dateTo]);

  const filteredPayments = payments.filter((payment) => {
    const date = payment.paymentDate || "";
    return (!year || year === "all" || date.startsWith(year))
      && (!month || month === "all" || date.slice(5, 7) === month)
      && (!dateFrom || date >= dateFrom)
      && (!dateTo || date <= dateTo);
  });
  const totalsByCurrency = data?.totalsByCurrency || {};
  const totalReceived = Object.entries(totalsByCurrency)
    .map(([code, amount]) => formatMoney(amount, code)).join(" · ") || "—";
  const lastUpdated = data?.lastUpdated || stats.scrapedAt || paymentsData.scrapedAt;

  return (
    <AppLayout title="TheBlowers Hub" overline="Extension data" actions={
      <button type="button" onClick={refreshFromExtension} disabled={refreshing || loading}
        className="inline-flex items-center gap-2 rounded-md border border-teal-500/40 px-3 py-2 text-sm font-medium text-teal-200 transition hover:bg-teal-500/10 disabled:opacity-50"
        data-testid="theblowers-refresh">
        <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
        {refreshing ? "Refreshing…" : "Refresh from extension"}
      </button>
    }>
      <PlatformHubTabs platform="theblowers" platformName="TheBlowers">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 pb-3 text-sm">
        <div className="text-zinc-300">Account <span className="ml-2 font-semibold text-white">{data?.username || "Not captured"}</span></div>
        <div className="text-xs text-zinc-500">Last updated {lastUpdated ? new Date(lastUpdated).toLocaleString() : "—"}</div>
      </div>

      {!loading && !data?.stats && !data?.payments && (
        <div className="mb-5 border-l-2 border-teal-400 bg-zinc-900 px-4 py-3 text-sm text-zinc-300">
          Visit the TheBlowers stats page and Invoice History page with the extension installed, then refresh this hub.
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Registrations this month" value={formatNumber(stats.registrationsThisMonth)} icon={UserPlus} />
        <Kpi label="All-time registrations" value={formatNumber(stats.allTimeRegistrations)} icon={Users} />
        <Kpi label="All-time clicks" value={formatNumber(stats.allTimeClicks)} icon={MousePointerClick} />
        <Kpi label="Received payments" value={totalReceived} icon={Banknote} detail={`${data?.paymentCount ?? payments.length} invoices`} />
        <Kpi label="Payment count" value={formatNumber(data?.paymentCount ?? payments.length)} icon={Banknote} />
        <Kpi label="Last payment" value={formatDate(data?.lastPaymentDate || paymentsData.lastPaymentDate)} icon={CalendarDays} />
        {Object.entries(data?.averagePaymentByCurrency || {}).map(([code, average]) => (
          <Kpi key={code} label={`Average payment · ${code}`} value={formatMoney(average, code)} icon={Banknote} />
        ))}
      </div>

      <section className="mb-5 flex flex-wrap items-end gap-3 border-y border-zinc-800 py-4" aria-label="Registration filters">
        <label className="grid gap-1 text-xs text-zinc-400">Year
          <select value={year} onChange={(event) => setYear(event.target.value)} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
            <option value="all">All years</option>{years.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-xs text-zinc-400">Month
          <select value={month} onChange={(event) => setMonth(event.target.value)} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
            <option value="all">All months</option>
            {Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0")).map((value) => (
              <option key={value} value={value}>{new Date(2026, Number(value) - 1, 1).toLocaleString(undefined, { month: "long" })}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-xs text-zinc-400">From
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white" />
        </label>
        <label className="grid gap-1 text-xs text-zinc-400">To
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white" />
        </label>
        <label className="grid gap-1 text-xs text-zinc-400">Revenue currency
          <select value={currency} onChange={(event) => setCurrency(event.target.value)} className="rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-sm text-white">
            {currencies.length ? currencies.map((code) => <option key={code} value={code}>{code}</option>) : <option value="EUR">EUR</option>}
          </select>
        </label>
        <button type="button" onClick={() => { setYear("all"); setMonth("all"); setDateFrom(""); setDateTo(""); }} className="rounded border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800">Clear filters</button>
      </section>

      <div className="mb-6 grid gap-5 xl:grid-cols-2">
        <ChartSection title={`Monthly revenue · ${currency}`} testid="theblowers-monthly-revenue-chart">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={filteredMonthlyRevenue} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} />
              <Tooltip formatter={(value) => formatMoney(value, currency)} contentStyle={{ background: "#09090b", border: "1px solid #3f3f46" }} />
              <Bar dataKey="total" name="Received" fill="#14b8a6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>
        <ChartSection title="Monthly registrations" testid="theblowers-monthly-registrations-chart">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={filteredMonthlyRegistrations} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} />
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #3f3f46" }} />
              <Bar dataKey="registrations" fill="#38bdf8" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>
        <ChartSection title="Daily registrations · selected period" testid="theblowers-daily-registrations-chart" className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={filteredDaily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#a1a1aa" fontSize={10} minTickGap={24} />
              <YAxis stroke="#a1a1aa" fontSize={11} />
              <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #3f3f46" }} />
              <Bar dataKey="registrations" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>
      </div>

      <section className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900" data-testid="theblowers-recent-payments">
        <div className="border-b border-zinc-800 px-4 py-3"><h2 className="text-sm font-semibold text-white">Recent payments</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] text-left text-sm">
            <thead className="text-xs uppercase text-zinc-500"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Invoice</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Download</th></tr></thead>
            <tbody className="divide-y divide-zinc-800">
              {[...filteredPayments].sort((left, right) => String(right.paymentDate || "").localeCompare(String(left.paymentDate || ""))).slice(0, 20).map((payment, index) => (
                <tr key={payment.downloadId || payment.invoiceNumber || `${payment.paymentDate}-${index}`}>
                  <td className="px-4 py-3 text-zinc-300">{payment.displayDate || formatDate(payment.paymentDate)}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300">{payment.invoiceNumber || "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-teal-200">{formatMoney(payment.amount, payment.currency)}</td>
                  <td className="px-4 py-3">{payment.downloadUrl ? <a href={payment.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline">Invoice</a> : <span className="text-zinc-600">—</span>}</td>
                </tr>
              ))}
              {!filteredPayments.length && <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-500">No received invoice payments in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      </PlatformHubTabs>
    </AppLayout>
  );
}

function ChartSection({ title, testid, className = "", children }) {
  return <section className={`min-w-0 rounded-lg border border-zinc-800 bg-zinc-900 p-4 ${className}`} data-testid={testid}><h2 className="mb-3 text-sm font-semibold text-zinc-200">{title}</h2>{children}</section>;
}